import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import Select from 'react-select';
import useFetch from '../constants/useFetch';
import { API_BASE_URL } from '../constants';
import ButtonSpinner from '../components/ButtonSpinner';
import { useSelector } from 'react-redux';

const classSuffixOptions = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(v => ({ value: v, label: v }));

function splitClassName(name = '') {
  // If the name ends with one uppercase letter, treat it as suffix
  if (/^[\s\S]*[A-Z]$/.test(name) && name.length > 1) {
    const base = name.slice(0, -1);
    const suffix = name.slice(-1);
    if (/^[A-Z]$/.test(suffix) && base.trim().length > 0) {
      return { base, suffix };
    }
  }
  return { base: name, suffix: null };
}

const EditClass = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { user: userInfo } = useSelector((state) => state.auth);
  const isAdmin = userInfo?.role === 'admin';
//   console.log({uuid});
  

  // Always call hooks (don’t guard them)
  const { data, loading, error } = useFetch(
    `${API_BASE_URL}/api/web/admin/edit-class-page/${uuid}`,
    'editClassPage',
    isAdmin // if your useFetch supports a "enabled" flag
  );

  const initial = useMemo(() => {
    const classroom = data?.classroom;
    if (!classroom) return null;
    const { base, suffix } = splitClassName(classroom.class_name || '');
    return {
      className: base || '',
      classSuffix: suffix ? { value: suffix, label: suffix } : null,
      classLevel: classroom.category || '',
      classTeacher: data?.currentTeacher
        ? {
            value: data.currentTeacher.uuid,
            label: `${data.currentTeacher.title} ${data.currentTeacher.lastname} ${data.currentTeacher.firstname}`,
          }
        : null,
      maxStudents: classroom.max_students || 40,
      description: classroom.description || '',
    };
  }, [data]);

  const [formData, setFormData] = useState({
    className: '',
    classSuffix: null,
    classLevel: '',
    classTeacher: null,
    maxStudents: 40,
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (initial) setFormData(initial);
  }, [initial]);

  const teachers = data?.teachers || [];
  const teacherOptions = teachers.map((t) => ({
    value: t.uuid,
    label: `${t.title} ${t.lastname} ${t.firstname}`,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name) => (selected) => {
    setFormData((prev) => ({ ...prev, [name]: selected || null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');

    if (!formData.className || !formData.classLevel) {
      setErrMsg('Please fill all required fields: Class Name and Class Level.');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        className: formData.className.trim(),
        classSuffix: formData.classSuffix?.value || null,
        classLevel: formData.classLevel,
        classTeacher: formData.classTeacher?.value || null,
        maxStudents: formData.maxStudents,
        description: formData.description,
      };

      const res = await fetch(`${API_BASE_URL}/api/web/admin/edit-class/${uuid}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || 'Failed to update class');
      }

      alert('Class updated successfully!');
      navigate('/classes');
    } catch (err) {
      setErrMsg(err.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '42px',
      borderColor: '#d1d5db',
      backgroundColor: '#fff',
      '&:hover': { borderColor: '#3b82f6' },
      '[data-theme="dark"] &': { backgroundColor: '#1f2937', borderColor: '#4b5563', color: '#fff' },
    }),
    singleValue: (p) => ({ ...p, color: '#374151', '[data-theme="dark"] &': { color: '#fff' } }),
    option: (p, s) => ({
      ...p,
      backgroundColor: s.isSelected ? '#3b82f6' : '#fff',
      color: s.isSelected ? '#fff' : '#374151',
      '&:hover': { backgroundColor: s.isSelected ? '#3b82f6' : '#f3f4f6' },
      '[data-theme="dark"] &': {
        backgroundColor: s.isSelected ? '#3b82f6' : '#1f2937',
        color: s.isSelected ? '#fff' : '#d1d5db',
        '&:hover': { backgroundColor: s.isSelected ? '#3b82f6' : '#374151' },
      },
    }),
    menu: (p) => ({ ...p, backgroundColor: '#fff', '[data-theme="dark"] &': { backgroundColor: '#1f2937' } }),
    input: (p) => ({ ...p, color: '#374151', '[data-theme="dark"] &': { color: '#fff' } }),
  };

  // You can still render guard UI conditionally (after hooks have run)
  if (!isAdmin) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-red-600 text-center">
          Access Denied: Only admins can edit classes.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="w-12 h-12 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  const friendlyError = typeof error === 'string' ? error : error?.message ? error.message : '';

  return (
    <div className="space-y-6 p-6 bg-gray-100 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Edit Class</h1>
            <p className="text-blue-100">Update class details</p>
          </div>
          <Link
            to="/classes"
            className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg transition-colors"
          >
            <FaArrowLeft className="w-5 h-5" />
            <span>Back to Classes</span>
          </Link>
        </div>
      </div>

      {friendlyError && <div className="text-red-600 text-center mb-4">{friendlyError}</div>}
      {errMsg && <div className="text-red-600 text-center mb-4">{errMsg}</div>}

      {/* Form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8 border border-gray-200 dark:border-gray-600">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Class Name */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Class Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="className"
                value={formData.className}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="e.g., JSS1"
                required
              />
            </div>

            {/* Class Suffix */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Class Suffix
              </label>
              <Select
                value={formData.classSuffix}
                onChange={handleSelectChange('classSuffix')}
                options={classSuffixOptions}
                placeholder="Select Alphabet"
                isSearchable
                isClearable
                styles={customStyles}
              />
            </div>

            {/* Class Level */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Class Level <span className="text-red-500">*</span>
              </label>
              <select
                name="classLevel"
                value={formData.classLevel}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              >
                <option value="">Select Level</option>
                <option value="junior">Junior Secondary School</option>
                <option value="senior">Senior Secondary School</option>
              </select>
            </div>

            {/* Class Form Teacher */}
            <div className="space-y-2 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Class Form Teacher
              </label>
              <Select
                value={formData.classTeacher}
                onChange={handleSelectChange('classTeacher')}
                options={teacherOptions}
                placeholder="Select Teacher"
                isSearchable
                isClearable
                styles={customStyles}
              />
              <p className="text-xs text-gray-500 mt-1">
                Only teachers not already assigned as a form teacher elsewhere are listed (plus the current one).
              </p>
            </div>

            {/* Max Students */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Maximum Students
              </label>
              <input
                type="number"
                name="maxStudents"
                min="1"
                max="50"
                value={formData.maxStudents}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Description */}
            <div className="space-y-2 md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                name="description"
                rows="4"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter class description"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6">
            <Link
              to="/all-classes"
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <ButtonSpinner size="small" color="white" />
                  Updating...
                </>
              ) : (
                <>
                  <FaSave />
                  Update Class
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditClass;
